import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { getSimpleSailingsByProperty } from '@go-client/lib/api/itinerary';
import { updateUserPreferences } from '@go-client/lib/api/user';
import { getLocaleUTCDateString, isoDate, todayLocalIso } from '@go-shared/lib/time';
import { findInitials, hashStringToColor, toggleElem } from '@go-shared/lib/utils';
import { Property } from '@go-shared/types/property';
import { UserPreferences } from '@go-shared/types/user';
import { Dropdown, DropdownItem } from '@spark-labs/avocado-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import Page from './Page';
import PageWrapper from './PageWrapper';
import { getDayLocation } from '@go-shared/lib/sailing';
import {
  BubbleOverlay,
  BubbleButton,
  BubbleDrawer,
  BubbleAlert,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import PropertyForm from '@go-client/components/BasicForms/PropertyForm';
import PropertiesNavigation from '@go-client/components/Navigations/PropertiesNavigation';
import { canViewPropertyDetailsForm } from '@go-shared/lib/permissions';
import { resolveFeedPath, resolveItinerariesPath, resolvePlannerPath } from '@go-client/routes';

type SortByType = 'A-Z' | 'Z-A';

interface SailingInfo {
  currentDay: number;
  daysTotal: number;
  startDate: Date;
  sailingId: string;
  dayId: string;
  location?: string;
}

interface PropertyBundle {
  property: Property;
  iconColor: string;
  initials: string;
  isFavorite?: boolean;
  sailingInfo?: SailingInfo;
}

const Properties: React.FC<RouteComponentProps> = props => {
  const [propertyBundles, setPropertyBundles] = useState<PropertyBundle[] | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SortByType>('A-Z');
  const [showArchived, setShowArchived] = useState(false);
  const [showCreatePropertyPanel, setShowCreatePropertyPanel] = useState(false);

  const serverInfo = useServerInfo();
  const { brand } = serverInfo;
  const { myProperties, refreshContext: refreshAvailableProperties } = usePropertyInfo();
  const { user, preferences, refreshContext: refreshUserInfo } = useUserInfo();

  useEffect(() => {
    fetchPropertyBundles().then().catch(console.error);
  }, [myProperties]);

  async function fetchPropertyBundles() {
    const bundles: PropertyBundle[] = await Promise.all(
      myProperties.map(async property => {
        const sailings = await getSimpleSailingsByProperty(
          property._id,
          todayLocalIso(),
          todayLocalIso()
        );
        // only care about the last sailing
        const sailing = sailings.length ? sailings[sailings.length - 1] : null;

        let sailingInfo: SailingInfo | undefined;

        if (sailing) {
          const currentDayIndx = sailing.days.findIndex(d => isoDate(d.date) === todayLocalIso());
          const currentDayObj = sailing.days[currentDayIndx];

          sailingInfo = {
            sailingId: sailing._id,
            dayId: currentDayObj?._id,
            currentDay: currentDayIndx + 1,
            daysTotal: sailing.days.length,
            startDate: sailing.days[0].date,
            location: currentDayObj && getDayLocation(currentDayObj),
          };
        }

        return {
          property,
          iconColor: hashStringToColor(property.name),
          initials: findInitials(property.name, 3),
          sailingInfo,
        };
      })
    );

    setPropertyBundles(bundles);
  }

  const filteredPropertyBundles = useMemo(() => {
    if (!propertyBundles) return undefined;

    propertyBundles.sort((a, b) =>
      sortBy === 'A-Z'
        ? a.property.name.localeCompare(b.property.name)
        : b.property.name.localeCompare(a.property.name)
    );

    const { favoriteProperties } = preferences;
    const favoriteBundles: PropertyBundle[] = [];

    const nonFavoriteBundles = propertyBundles
      .map(bundle => ({
        ...bundle,
        isFavorite: favoriteProperties.includes(bundle.property._id),
      }))
      .filter(bundle => {
        if (bundle.isFavorite) {
          favoriteBundles.push(bundle);
        }

        return !bundle.isFavorite;
      });

    let result: PropertyBundle[] = [...favoriteBundles, ...nonFavoriteBundles];
    if (!showArchived) result = result.filter(a => a.property.active);

    return result;
  }, [propertyBundles, sortBy, showArchived, preferences, user]);

  function handleDropdownSelect(item: DropdownItem) {
    setSortBy(item.key === 'Z-A' ? 'Z-A' : 'A-Z');
  }

  function handlePropertyClick(bundle: PropertyBundle) {
    props.history.push(resolveItinerariesPath(bundle.property._id));
  }

  async function handlePropertyStarClick(bundle: PropertyBundle) {
    const newPreferences: UserPreferences = {
      ...preferences,
      favoriteProperties: toggleElem(preferences.favoriteProperties, bundle.property._id),
    };
    await updateUserPreferences(newPreferences);
    await refreshUserInfo({ skipUser: true });
  }

  function handlePropertySailingCanvasClick(bundle: PropertyBundle) {
    if (!bundle.sailingInfo) return;

    props.history.push(
      resolvePlannerPath(
        bundle.property._id,
        bundle.sailingInfo.sailingId,
        bundle.sailingInfo.dayId
      )
    );
  }

  function handlePropertySailingFeedClick(bundle: PropertyBundle) {
    if (!bundle.sailingInfo) return;

    props.history.push(
      resolveFeedPath(bundle.property._id, bundle.sailingInfo.sailingId, bundle.sailingInfo.dayId)
    );
  }

  return (
    <PageWrapper {...props}>
      <Page className="Properties absolute flex items-stretch">
        <MenuBar {...props} />
        <div className="PropertiesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="PropertiesHeader align-with-logo flex items-end sticky top-0
                left-0 z-10 bg-white shadow"
          >
            <BrandLogo logo={brand.logo} />

            <div className="mb-3 px-5 pl-8 flex-grow flex items-end">
              <PropertiesNavigation active="properties" />

              <div className="ml-auto" />

              <Dropdown
                items={[
                  { key: 'A-Z', element: 'A-Z' },
                  { key: 'Z-A', element: 'Z-A' },
                ]}
                onSelect={handleDropdownSelect}
                alignRight
              >
                {({ toggleRef, ...rest }) => (
                  <BubbleButton
                    variant="navy"
                    buttonRef={toggleRef}
                    {...rest}
                    inverse
                    className="whitespace-nowrap"
                    iconSuffix="caret-down"
                  >
                    Sort by: {sortBy}
                  </BubbleButton>
                )}
              </Dropdown>
              <div className="py-2 relative whitespace-nowrap">
                <BubbleInput
                  className="mr-2"
                  inputClassName="stretched-link"
                  type="checkbox"
                  checked={showArchived}
                  onValue={setShowArchived}
                />
                <span>Show Archived</span>
              </div>
              {canViewPropertyDetailsForm(user) && (
                <BubbleButton
                  variant="lime"
                  className="flex items-center ml-3"
                  onClick={() => setShowCreatePropertyPanel(true)}
                  icon="plus"
                >
                  New Property
                </BubbleButton>
              )}
            </div>
          </div>
          <div className="PropertiesBody mx-10 content-minus-logo">
            <div style={{ maxWidth: '1750px' }} className="py-8 mx-auto">
              <BubbleOverlay
                hideBg
                loading={!filteredPropertyBundles}
                style={{ minHeight: '50px' }}
              >
                {filteredPropertyBundles && (
                  <>
                    <div className="grid gap-8 lg:grid-cols-2">
                      {(filteredPropertyBundles || []).map(bundle => (
                        <div
                          key={bundle.property._id}
                          className="h-full w-full flex shadow-md bg-white overflow-hidden
                            rounded text-left transform hover:scale-105"
                          style={{
                            transitionProperty: 'transform',
                            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                            transitionDuration: '150ms',
                          }}
                        >
                          <div
                            className="flex-grow flex text-left items-stretch cursor-pointer"
                            onClick={() => handlePropertyClick(bundle)}
                          >
                            <div
                              className="flex-shrink-0 m-5 mr-0"
                              style={{
                                width: '5rem',
                                height: '5rem',
                                backgroundColor: bundle.iconColor,
                                borderRadius: '2.5rem',
                              }}
                            >
                              <div
                                className="text-2xl text-white w-full h-full
                                  text-center align-middle"
                                style={{ lineHeight: '5rem' }}
                              >
                                {bundle.initials}
                              </div>
                            </div>
                            <div className="p-5 flex-grow z-10">
                              <div className="flex">
                                <div className="text-2xl mr-auto">{bundle.property.name}</div>
                                {bundle.isFavorite !== undefined && (
                                  <BubbleButton
                                    variant={bundle.isFavorite ? 'banana' : 'smoke'}
                                    inverse
                                    className="-mt-3 -mr-3"
                                    onClick={() => handlePropertyStarClick(bundle)}
                                    icon={[bundle.isFavorite ? 'fas' : 'far', 'star']}
                                    iconSize="lg"
                                  />
                                )}
                              </div>
                              <div className="text-lg mb-3">{bundle.property.location}</div>
                              <div className="text-md">{bundle.property.description}</div>
                            </div>
                          </div>
                          {bundle.sailingInfo && (
                            <div className="flex-shrink-0 h-full" style={{ width: '200px' }}>
                              <div className="bg-grey-light h-full flex flex-col">
                                <div className="mb-auto flex-shrink-0 p-5">
                                  <div>
                                    <b className="text-navy">On day:&nbsp;</b>
                                    <span>
                                      {bundle.sailingInfo.currentDay} of{' '}
                                      {bundle.sailingInfo.daysTotal}
                                    </span>
                                  </div>
                                  <div>
                                    <b className="text-navy">Since:</b>
                                    {` ${getLocaleUTCDateString(bundle.sailingInfo.startDate)}`}
                                  </div>
                                  <div>
                                    <b className="text-navy">Located: </b>
                                    {` ${bundle.sailingInfo.location || 'Unknown'}`}
                                  </div>
                                </div>
                                <div className="p-3 grid gap-1 grid-cols-1">
                                  <BubbleButton
                                    variant="navy"
                                    block
                                    onClick={() => handlePropertySailingCanvasClick(bundle)}
                                    className="text-left"
                                    icon={['fass', 'chart-tree-map']}
                                  >
                                    Daily Planner
                                  </BubbleButton>
                                  <BubbleButton
                                    variant="navy"
                                    block
                                    onClick={() => handlePropertySailingFeedClick(bundle)}
                                    className="text-left"
                                    icon={['fass', 'newspaper']}
                                  >
                                    Feed Editor
                                  </BubbleButton>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {filteredPropertyBundles.length < 1 && (
                      <BubbleAlert variant="banana">
                        You do not have access to any properties, or there are no properties
                        available at the moment.
                      </BubbleAlert>
                    )}
                  </>
                )}
              </BubbleOverlay>
            </div>
          </div>
          {showCreatePropertyPanel && (
            <BubbleDrawer
              onClose={() => setShowCreatePropertyPanel(false)}
              containerClassName="bg-eggshell"
            >
              <PropertyForm
                appearance="panel"
                onClose={() => setShowCreatePropertyPanel(false)}
                onFormSubmitted={refreshAvailableProperties}
              />
            </BubbleDrawer>
          )}
        </div>
      </Page>
    </PageWrapper>
  );
};

export default Properties;
